import { useState } from 'react';
import { useLocation, Navigate, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography
} from '@mui/material';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  CreditCardOutlined,
  SafetyCertificateOutlined,
  TagsOutlined,
  UserOutlined
} from '@ant-design/icons';
import axios from 'axios.js';
import Logo from 'components/Logo/Logo';

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

// ---------------------------------------------------------------------------
// Shared style tokens - lifted from the Sign Up page so this screen reads as
// the same product: white card on a soft tinted background, navy labels
// sitting above thin bordered "field" boxes, blue primary actions.
// ---------------------------------------------------------------------------
const BRAND_BLUE = '#2F6FED';
const BRAND_NAVY = '#1B2A4A';
const BORDER_COLOR = '#D9DEE7';

const labelSx = {
  fontSize: 13.5,
  fontWeight: 600,
  color: BRAND_NAVY,
  mb: 0.75,
  display: 'flex',
  alignItems: 'center',
  gap: 0.75
};

const fieldBoxSx = {
  border: `1px solid ${BORDER_COLOR}`,
  borderRadius: 1.75,
  px: 1.75,
  py: 1.25,
  bgcolor: '#fff'
};

const primaryBtnSx = {
  bgcolor: BRAND_BLUE,
  borderRadius: 2,
  textTransform: 'none',
  fontWeight: 700,
  fontSize: 16,
  py: 1.4,
  boxShadow: 'none',
  '&:hover': { bgcolor: '#2559C7', boxShadow: 'none' }
};

const outlinedBtnSx = {
  borderRadius: 2,
  textTransform: 'none',
  fontWeight: 600,
  borderColor: BORDER_COLOR,
  color: BRAND_NAVY,
  '&:hover': { borderColor: BRAND_BLUE, bgcolor: 'rgba(47,111,237,0.04)' }
};

const outerWrapperSx = {
  minHeight: '100vh',
  bgcolor: '#F4F6FB',
  backgroundImage:
    'radial-gradient(circle at 12% 18%, rgba(47,111,237,0.06), transparent 42%), radial-gradient(circle at 88% 82%, rgba(47,111,237,0.06), transparent 42%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  px: 2,
  py: 6
};

const cardSx = {
  width: '100%',
  maxWidth: 480,
  bgcolor: '#fff',
  borderRadius: 3,
  boxShadow: '0 10px 40px rgba(15, 23, 42, 0.08)',
  p: { xs: 3, sm: 5 }
};



// Plan object accepts these fields, all optional except PlanID/PlanName/Price
// so this keeps working unchanged if your backend doesn't send them yet:
//   description   - short tagline shown under the plan name
//   badge         - small chip label, e.g. "Most Popular"
//   validity      - e.g. "12 Months" (shown instead of billing_cycle if present)
//   billing_cycle - e.g. "month", "year" (fallback if validity absent)
//   originalPrice - pre-discount price, shown struck through if higher than Price
//   basePrice     - pre-tax price, shown as its own line if provided together with taxAmount
//   taxAmount     - GST amount, shown as its own line if provided together with basePrice
//   gstRate       - e.g. 18, shown next to the GST line label if provided
//   features      - array of strings

const ActualPaymetnForm = () =>
{
  const location = useLocation();
  const navigate = useNavigate();
  const { user, plan } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Separate flag: true only when the payment itself succeeded on Razorpay's
  // side but our backend signature verification failed. In that case the
  // user isn't "retrying a payment" — they need to log in so support/the
  // account page can reconcile the transaction against their account.
  const [verificationFailed, setVerificationFailed] = useState(false);

  // Payment verified successfully — shows the "Payment Successful" / invoice screen.
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  // { invoiceNumber, invoiceUrl } once the backend has generated it.
  const [invoice, setInvoice] = useState(null);
  const [invoicePending, setInvoicePending] = useState(false);

  // Guard: someone navigated here directly without going through registration
  if (!user || !plan) {
    return <Navigate to="/choose-plan" replace />;
  }

    // Asks the backend to generate the GST invoice for this purchase.
    // Backend owns invoice numbering / GST (CGST+SGST vs IGST) computation,
    // since it needs to be sequential and tied to the seller's home state.
    //
    // Expected request/response contract (adjust to match your API):
    //   POST /generate-invoice
    //   body: { UserID, tenant, PlanID, OrderID, PaymentID, BillingName,
    //           CompanyName, Email, MobileNumber, Address, GSTIN, State, Amount }
    //   response: { Status: 1, InvoiceNumber, InvoiceUrl }
    //          or { Status: 0, Message }
    const generateInvoice = async (razorpayResponse, orderData) => {
        try {
            const { data } = await axios.post('/generate-invoice', {
                UserID: user.id,
                tenant: user.tenant,
                PlanID: plan.PlanID,
                OrderID: razorpayResponse.razorpay_order_id,
                PaymentID: razorpayResponse.razorpay_payment_id,
                BillingName: user.name,
                CompanyName: user.companyName,
                Email: user.email,
                MobileNumber: user.mobileNumber,
                Address: user.address,
                GSTIN: user.gstin,
                State: user.state,
                Amount: plan.Price
            });

            if (data && data.Status === 1) {
                return { invoiceNumber: data.InvoiceNumber, invoiceUrl: data.InvoiceUrl };
            }
            console.log('Invoice generation returned failure -', data && data.Message);
            return null;
        }
        catch (invoiceErr)
        {
            console.log('Invoice generation failed -', invoiceErr);
            return null;
        }
    };

    const handlePayment = async () =>
    {
        setLoading(true);
        setError('');
        setVerificationFailed(false);

        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
        setError('Failed to load Razorpay. Check your connection and try again.');
        setLoading(false);
        return;
        }

        try {
        // Step 1: create order on backend - amount is derived server-side from plan.id, never trust client amount
        const { data: orderData } = await axios.post('/payment-create-order', {
            plan_id: plan.PlanID,
            name: user.name,
            email: user.email,
            mobile_number: user.mobileNumber,
            Price:plan.Price,
            UserID:user.id,
            tenant:user.tenant,
            UserSubscribedPlanID:user.UserSubscribedPlanID
        });

        // Step 2: open Razorpay Checkout
        const options = {
                key: orderData.key,
                amount: orderData.amount,
                currency: orderData.currency,
                order_id: orderData.order_id,
                name: 'Sterling Systems Pvt. Ltd.',
                description: `Payment for KiT ${plan.PlanName}`,
                prefill: {
                name: user.name,
                email: user.email,
                contact: user.mobileNumber
                },
                theme: { color: '#3399cc' },
                handler: async function (response) {
                try {
                    // Step 3: verify on backend
                    const { data: verifyData } = await axios.post('/verify-payment', {
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature
                    });

                    if (verifyData.success) {
                    // Step 4: generate the GST invoice now that payment is verified.
                    // Payment success matters more than the invoice call, so a
                    // failure here does not block the user — they can always
                    // fetch the invoice later from their account.
                        const invoiceResult = await generateInvoice(response, orderData);
                        if (invoiceResult) {
                            setInvoice(invoiceResult);
                        } else {
                            setInvoicePending(true);
                        }
                        setPaymentSuccess(true);
                    } else {
                        setError('Payment verification failed. Please contact support.');
                        setVerificationFailed(true);
                    }
                }
                catch (verifyErr)
                {
                    setError('Payment verification failed. Please contact support.');
                    setVerificationFailed(true);
                } finally {
                    setLoading(false);
                }
                },
                modal: {
                    ondismiss: async function () {

                        try
                        {
                            await axios.post('/payment-cancelled', {
                                order_id: orderData.order_id,      // from Step 1 response, available via closure
                                tenant: localStorage.getItem("tenant")
                            });

                        }
                        catch (cancelErr)
                        {
                            console.log("Payment Cancellation update failed -");
                            console.log(cancelErr);
                        }

                        setError('Payment was cancelled.');
                        setLoading(false);
                    }
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', async function (response) {
                try {
                    await axios.post('/payment-failed', {
                        order_id: response.error.metadata.order_id,
                        payment_id: response.error.metadata.payment_id,
                        reason: response.error.description,
                        tenant: localStorage.getItem("tenant")
                    });

                } catch (failErr) {
                    console.log(failErr);
                }

                setError(`Payment failed: ${response.error.description}`);
                setLoading(false);
            });
        rzp.open();
        }
        catch (err)
        {
        console.log(err);
        setError('Could not start payment. Please try again.');
        setLoading(false);
        }
    };

  // ---- Payment successful screen (with invoice download once ready) ----
  if (paymentSuccess) {
    return (
      <Box sx={outerWrapperSx}>
        <Box sx={{ ...cardSx, textAlign: 'center' }}>
          <Box sx={{ mb: 3.5 }}>
            <Logo />
          </Box>

          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: 'rgba(46,125,50,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2
            }}
          >
            <CheckCircleOutlined style={{ fontSize: 30, color: '#2e7d32' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: BRAND_NAVY, mb: 1 }}>
            Payment Successful
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Thank you, {user.name}. Your payment of ₹{plan.Price} for the {plan.PlanName} plan has been received.
          </Typography>

          {invoice ? (
            <Box sx={{ ...fieldBoxSx, textAlign: 'left', mb: 3 }}>
              <Typography sx={{ ...labelSx, mb: 0.25 }}>Invoice Number</Typography>
              <Typography sx={{ fontWeight: 700 }}>{invoice.invoiceNumber}</Typography>
            </Box>
          ) : invoicePending ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your invoice is still being generated. It will be emailed to you, and will also be
              available from your account page shortly.
            </Typography>
          ) : null}

          <Stack spacing={1.5}>
            {invoice && invoice.invoiceUrl && (
              <Button
                fullWidth
                size="large"
                variant="outlined"
                sx={outlinedBtnSx}
                onClick={() => window.open(invoice.invoiceUrl, '_blank', 'noopener,noreferrer')}
              >
                Download Invoice
              </Button>
            )}
            <Button
              fullWidth
              size="large"
              variant="contained"
              sx={primaryBtnSx}
              onClick={() => navigate('/otp-verification', { state: { user, invoice } })}
            >
              Continue
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }

  const hasTaxBreakup = plan.basePrice != null && plan.taxAmount != null;
  const hasDiscount = plan.originalPrice != null && plan.originalPrice > plan.Price;
  const validityLabel = plan.validity
    ? `Valid for ${plan.validity}`
    : plan.billing_cycle
      ? `Billed per ${plan.billing_cycle}`
      : null;

  return (
    <Box sx={outerWrapperSx}>
      <Box sx={cardSx}>
        <Box sx={{ mb: 3.5 }}>
          <Logo />
        </Box>

        {/* Header row - mirrors "Sign up" / "Already have an account?" */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: BRAND_NAVY }}>
            Complete Your Payment
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary', mt: 0.5 }}>
            <SafetyCertificateOutlined style={{ fontSize: 13 }} />
            <Typography variant="caption">Secured by Razorpay</Typography>
          </Stack>
        </Stack>

        {/* Registering as */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={labelSx}>
            <UserOutlined style={{ fontSize: 13 }} />
            Registering as
          </Typography>
          <Box sx={fieldBoxSx}>
            <Typography sx={{ fontWeight: 600 }}>{user.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email} · {user.mobileNumber}
            </Typography>
          </Box>
        </Box>

        {/* Plan + Validity */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={labelSx}>
              <TagsOutlined style={{ fontSize: 13 }} />
              Plan
            </Typography>
            <Box sx={fieldBoxSx}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography sx={{ fontWeight: 600 }}>{plan.PlanName}</Typography>
                {plan.badge && (
                  <Chip
                    label={plan.badge}
                    size="small"
                    sx={{ bgcolor: 'rgba(47,111,237,0.1)', color: BRAND_BLUE, fontWeight: 600, height: 20 }}
                  />
                )}
              </Stack>
              {plan.ValidityDisplay && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Validity : {plan.ValidityDisplay}
                </Typography>
              )}

              {plan.NoOfContacts && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  No Of Contacts : {plan.NoOfContacts}
                </Typography>
              )}
               {plan.NoOfGroups && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  No Of Groups : {plan.NoOfGroups}
                </Typography>
              )}
            </Box>
          </Box>

          {validityLabel && (
            <Box sx={{ flex: 1 }}>
              <Typography sx={labelSx}>
                <CalendarOutlined style={{ fontSize: 13 }} />
                Validity
              </Typography>
              <Box sx={{ ...fieldBoxSx, display: 'flex', alignItems: 'center' }}>
                <Typography>{validityLabel}</Typography>
              </Box>
            </Box>
          )}
        </Stack>

        {/* Features */}
        {plan.features && plan.features.length > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={labelSx}>What&apos;s included</Typography>
            <Stack spacing={0.75}>
              {plan.features.map((feature, idx) => (
                <Stack direction="row" spacing={1} alignItems="flex-start" key={idx}>
                  <CheckCircleOutlined style={{ fontSize: 16, color: '#2e7d32', marginTop: 3 }} />
                  <Typography variant="body2">{feature}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        {/* GSTIN, styled + captioned the same way the Sign Up page treats it */}
        {user.gstin && (
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={labelSx}>Billing GSTIN</Typography>
            <Box sx={fieldBoxSx}>{user.gstin}</Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              Your GST invoice will be issued against this GSTIN.
            </Typography>
          </Box>
        )}

        {/* Price summary */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={labelSx}>
            <CreditCardOutlined style={{ fontSize: 13 }} />
            Price Summary
          </Typography>
          <Box sx={fieldBoxSx}>
            <Stack spacing={0.75}>
              {hasDiscount && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Original Price
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                    ₹{plan.originalPrice}
                  </Typography>
                </Stack>
              )}
              {hasTaxBreakup ? (
                <>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Base Price
                    </Typography>
                    <Typography variant="body2">₹{plan.basePrice}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      GST{plan.gstRate ? ` (${plan.gstRate}%)` : ''}
                    </Typography>
                    <Typography variant="body2">₹{plan.taxAmount}</Typography>
                  </Stack>
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Inclusive of all applicable taxes
                </Typography>
              )}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ pt: 1, mt: 0.5, borderTop: `1px solid ${BORDER_COLOR}` }}
              >
                <Typography variant="subtitle1" fontWeight={700} color={BRAND_NAVY}>
                  Total
                </Typography>
                <Typography variant="h4" sx={{ color: BRAND_BLUE }} fontWeight={700}>
                  ₹{plan.Price}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Box>

        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {verificationFailed ? (
          <Stack spacing={1.5}>
            <Button
              fullWidth
              size="large"
              variant="contained"
              sx={primaryBtnSx}
              onClick={() => navigate('/login', { state: { email: user.email } })}
            >
              Login
            </Button>
            <Button
              fullWidth
              size="medium"
              variant="text"
              disabled={loading}
              sx={{ textTransform: 'none', fontWeight: 600, color: BRAND_NAVY }}
              onClick={handlePayment}
            >
              Try Payment Again
            </Button>
          </Stack>
        ) : (
          <Button
            fullWidth
            size="large"
            variant="contained"
            disabled={loading}
            sx={primaryBtnSx}
            onClick={handlePayment}
          >
            {loading ? 'Processing...' : `Pay ₹${plan.Price}`}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ActualPaymetnForm;
